import { useCallback, useEffect, useMemo, useState } from 'react';
import SiteFooter from '../components/layout/SiteFooter';
import StoreDownloadButton from '../components/store/StoreDownloadButton';
import { appLinkConfig } from '../constants/appLinks';
import { introAssets } from '../constants/assets';
import {
  introHighlights,
  introNavigatorItems,
  introSignals,
  introStats,
  institutionLogos,
  newsItems,
  partnerFeatures,
  riderFeatures,
} from '../data/siteContent';
import logo from '../logo.svg';
import { detectPlatform, getAppLinkTarget, openAppWithStoreFallback } from '../utils/appLinking';

function IntroLanding() {
  const platform = useMemo(detectPlatform, []);
  const target = useMemo(() => getAppLinkTarget(platform), [platform]);
  const [redirectState, setRedirectState] = useState(
    platform === 'desktop' ? 'desktop' : 'opening',
  );
  const [activeSectionId, setActiveSectionId] = useState(introNavigatorItems[0].id);

  useEffect(() => {
    if (platform === 'desktop' || !target) {
      return undefined;
    }

    setRedirectState('opening');
    return openAppWithStoreFallback(target, () => setRedirectState('store'));
  }, [platform, target]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    let frameId = null;

    const updateActiveSection = () => {
      frameId = null;

      if (document.documentElement.scrollHeight <= window.innerHeight + 1) {
        return;
      }

      const anchor = window.scrollY + window.innerHeight * 0.42;
      let nextActiveSectionId = introNavigatorItems[0].id;

      introNavigatorItems.forEach((item) => {
        const section = document.getElementById(item.id);

        if (!section) {
          return;
        }

        const sectionTop = section.getBoundingClientRect().top + window.scrollY;

        if (sectionTop <= anchor) {
          nextActiveSectionId = item.id;
        }
      });

      setActiveSectionId((currentSectionId) => (
        currentSectionId === nextActiveSectionId ? currentSectionId : nextActiveSectionId
      ));
    };

    const scheduleActiveUpdate = () => {
      if (frameId !== null) {
        return;
      }

      if (typeof window.requestAnimationFrame === 'function') {
        frameId = window.requestAnimationFrame(updateActiveSection);
        return;
      }

      updateActiveSection();
    };

    updateActiveSection();
    window.addEventListener('scroll', scheduleActiveUpdate, { passive: true });
    window.addEventListener('resize', scheduleActiveUpdate);

    return () => {
      window.removeEventListener('scroll', scheduleActiveUpdate);
      window.removeEventListener('resize', scheduleActiveUpdate);

      if (frameId !== null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  const handleNavigatorClick = useCallback((sectionId) => {
    const section = document.getElementById(sectionId);

    if (!section) {
      return;
    }

    setActiveSectionId(sectionId);

    const sectionTop = section.getBoundingClientRect().top + window.scrollY;

    if (typeof window.scrollTo === 'function') {
      const targetTop = Math.max(sectionTop - 24, 0);
      const scrollToWindow = window.scrollTo.bind(window);
      const scrollToTarget = (behavior) => scrollToWindow({ top: targetTop, behavior });

      scrollToTarget('smooth');
      window.setTimeout(() => {
        if (Math.abs(window.scrollY - targetTop) > 4) {
          scrollToTarget('auto');
        }
      }, 700);
      return;
    }

    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleOpenApp = () => {
    if (target) {
      window.location.href = target.openUrl;
    }
  };

  const handleOpenStore = () => {
    if (target) {
      window.location.href = target.storeUrl;
    }
  };

  return (
    <div className="intro-shell">
      <header className="intro-header">
        <div className="intro-brand" aria-label="말타 랜딩">
          <img src={logo} alt="" />
          <span>Marlta</span>
        </div>
      </header>

      <nav className="intro-side-nav" aria-label="랜딩 섹션 이동">
        {introNavigatorItems.map((item) => {
          const isActive = activeSectionId === item.id;

          return (
            <button
              className={`intro-side-nav__item${isActive ? ' is-active' : ''}`}
              type="button"
              aria-current={isActive ? 'location' : undefined}
              aria-label={`${item.label} 섹션으로 이동`}
              onClick={() => handleNavigatorClick(item.id)}
              key={item.id}
            >
              <span className="intro-side-nav__dot" aria-hidden="true" />
              <span className="intro-side-nav__label">{item.label}</span>
            </button>
          );
        })}
      </nav>

      <main className="intro-main">
        <section className="intro-hero" id="intro-home" aria-labelledby="intro-title">
          <img className="intro-hero-bg" src={introAssets.ridingHero} alt="" aria-hidden="true" />
          <div className="intro-copy">
            <span className="intro-kicker">Horse Riding Platform</span>
            <h1 id="intro-title">
             <small> 승마 예약의 시작,</small>
              <br />
              말타
            </h1>
            <p>
              말 위에서의 특별한 순간을 더 쉽게.
              <br />
              가까운 승마장을 찾고, 원하는 시간에 예약하세요.
            </p>
            <br/>

            <h3>지금 내 주변 승마장 확인하기</h3>
            <div className="intro-actions" aria-label="앱 다운로드 링크">
              <StoreDownloadButton
                href={appLinkConfig.iosStoreUrl}
                label="APP STORE"
                type="apple"
                variant="dark"
              />
              <StoreDownloadButton
                href={appLinkConfig.androidStoreUrl}
                label="PLAY STORE"
                type="play"
              />
            </div>

            <div className="intro-signal-row" aria-label="말타에서 확인 가능한 정보">
              {introSignals.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>

            {platform !== 'desktop' ? (
              <div className="mobile-redirect-panel" role="status" aria-live="polite">
                <strong>
                  {redirectState === 'opening'
                    ? '말타 앱으로 연결하고 있어요.'
                    : '앱이 열리지 않으면 스토어로 이동합니다.'}
                </strong>
                <div>
                  <button className="text-action" type="button" onClick={handleOpenApp}>
                    앱 열기
                  </button>
                  <button className="text-action" type="button" onClick={handleOpenStore}>
                    스토어로 이동
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="intro-hero-panel" aria-hidden="true">
            <span>오늘 이용 가능한</span>
            <strong>승마장 · 프로그램 · 시간</strong>
            <small>앱에서 한 번에 확인</small>
          </div>
        </section>

        <section className="intro-overview-section" aria-label="말타 주요 흐름">
          <div className="intro-stat-row">
            {introStats.map((item) => (
              <div className="intro-stat" key={item.label}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="intro-feature-section" id="intro-booking" aria-labelledby="rider-feature-title">
          <div className="intro-section-heading">
            <span className="intro-kicker">Booking Steps</span>
            <h2 id="rider-feature-title">찾는 순간부터 예약 완료까지</h2>
            <p>
              승마를 처음 시작하는 사람도 필요한 정보를 놓치지 않도록 탐색, 비교, 예약
              흐름을 선명하게 정리합니다.
            </p>
          </div>

          <div className="intro-highlight-row" aria-label="말타 주요 가치">
            {introHighlights.map((item) => (
              <div className="intro-highlight" key={item.label}>
                <strong>{item.value}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <div className="intro-feature-layout">
            <div className="intro-image-stack" aria-hidden="true">
              <img className="intro-photo intro-photo--portrait" src={introAssets.appRider} alt="" />
              <img className="intro-phone-image" src={introAssets.reservation} alt="" />
            </div>
            <div className="intro-feature-list">
              {riderFeatures.map((feature) => (
                <article className="intro-feature" key={feature.title}>
                  <h3>{feature.title}</h3>
                  <p>{feature.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="intro-coverage-section" id="intro-network" aria-labelledby="coverage-title">
          <img
            className="intro-coverage-bg"
            src={introAssets.coverageMap}
            alt=""
            aria-hidden="true"
          />
          <div className="intro-coverage-copy">
            <span className="intro-kicker">National Network</span>
            <h2 id="coverage-title">전국 100개 이상의 승마장과 함께하는</h2>
            <p>
              말타는 전국 주요 승마장과의 파트너 협약을 바탕으로, 가까운 곳부터 인기
              승마장까지 앱에서 예약 가능한 선택지를 넓혀갑니다.
            </p>
          </div>
        </section>

        <section className="intro-blue-band" id="intro-app" aria-labelledby="intro-blue-title">
          <div className="intro-blue-copy">
            <span className="intro-kicker">Marlta App</span>
            <h2 id="intro-blue-title">승마가 궁금한 순간,<br/>앱에서 바로 이어지게</h2>
            <p>
              말타는 승마장 정보, 프로그램 가격, 예약 가능 시간, 강습 내역을 사용자 중심으로
              정리해 더 가볍게 승마를 시작하게 돕습니다.
            </p>
          </div>
          <div className="intro-blue-device" aria-hidden="true">
            <img src={introAssets.member} alt="" />
          </div>
        </section>

        <section className="intro-partner-section" id="intro-partners" aria-labelledby="partner-title">
          <img
            className="intro-partner-bg"
            src={introAssets.partnerOffice}
            alt=""
            aria-hidden="true"
          />
          <div className="intro-partner-copy">
            <span className="intro-kicker">Partners</span>
            <h2 id="partner-title">승마장 운영과 홍보도 말타에서</h2>
            <p>
              말타를 통해 승마장 관리, 홍보는 물론 신규회원 유치가 쉽도록 돕습니다. <br/>PC와
              모바일에서 회원 관리, 마필 관리, 마장 관리, 프로모션 관리를 이어갈 수 있습니다.
            </p>
            <div className="partner-chip-row">
              {partnerFeatures.map((feature) => (
                <span key={feature}>{feature}</span>
              ))}
            </div>
          </div>
          {/* <div className="intro-partner-visual intro-partner-visual--photo" aria-hidden="true">
            <img src={introAssets.management} alt="" />
          </div> */}
        </section>

        <section className="intro-news-partners-section" id="intro-news" aria-labelledby="intro-news-title">
          <div className="intro-section-heading">
            <span className="intro-kicker">Marlta Story</span>
            <h2 id="intro-news-title">말타뉴스</h2>
            <p>
              말타의 서비스 소식과 승마에 대한 다양한 정보를 볼 수 있습니다.
            </p>
          </div>

          <div className="intro-news-partners-grid">
            <div className="intro-news-block">
              <h3>말타뉴스</h3>
              <div className="intro-mini-card-grid">
                {newsItems.map((item) => (
                  <article className="intro-mini-card" key={item.title}>
                    <span>{item.category}</span>
                    <h4>{item.title}</h4>
                    <p>{item.body}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="intro-final-section" id="intro-download" aria-labelledby="intro-final-title">
          <div>
            <span className="intro-kicker">Start</span>
            <h2 id="intro-final-title">말타로 오늘의 승마를 시작해보세요</h2>
            <p>보다 편리한 승마 서비스를 앱에서 바로 경험할 수 있습니다.</p>
          </div>
          <div className="intro-final-download">
            <strong>지금 내 주변 승마장 확인하기</strong>
            <div className="intro-actions intro-actions--compact" aria-label="앱 다운로드 링크">
              <StoreDownloadButton
                href={appLinkConfig.iosStoreUrl}
                label="APP STORE"
                type="apple"
                variant="dark"
              />
              <StoreDownloadButton
                href={appLinkConfig.androidStoreUrl}
                label="PLAY STORE"
                type="play"
              />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter variant="intro" />
      <section className="intro-institution-strip" aria-label="협력 기관 및 협회">
        <div className="intro-institution-strip__viewport">
          <div className="intro-institution-strip__track">
            <div className="intro-institution-strip__group">
              {institutionLogos.map((item) => (
                <img src={item.image} alt={item.label} key={item.label} />
              ))}
            </div>
            <div className="intro-institution-strip__group" aria-hidden="true">
              {institutionLogos.map((item) => (
                <img src={item.image} alt="" key={`loop-${item.label}`} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default IntroLanding;
